import React, { useState } from "react";
import OptionsGroup from "./components/OptionsGroup/OptionsGroup";
import FilterTabs from "@/app-desktop/containers/Filters/components/FilterTabs";

// hooks
import { useTranslation } from "react-i18next";
import useAirportFilter from "@/hooks/useAirportFilter";

// utils
import { getFilterCityIndex } from "@/utils/content.utils";
import { getAirportFilterSections } from "@/utils/compute.utils";
import { buildTestId } from "@/utils/helpers.utils";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";

// styles
import Styled from "./AirportFilter.styled";

// types
import { FLIGHT_LEG_TYPE_MAP } from "@tajawal/web-flights-sdk/lib/constants/filters.constants";
import type NestedSelectFilter from "@tajawal/web-flights-sdk/lib/stores/filter/base/nested-select-filter.store";

export type AirportFilterProps = {
  testIdPrefix: string;
  tabs: { code: number; label: string }[];
  requestAirports: string[][];
  filter: NestedSelectFilter;
  isStepByStep: boolean;
  currentStep: string;
};

function AirportFilter({
  testIdPrefix,
  tabs,
  requestAirports,
  filter,
  currentStep,
  isStepByStep,
}: AirportFilterProps): JSX.Element {
  const testId = buildTestId(testIdPrefix, "Airport");
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const { resources, originFilterState, destinationFilterState, handleSelect } =
    useAirportFilter(filter, activeIndex);

  const cityIndex = getFilterCityIndex(isStepByStep, currentStep);

  const originAirports = getAirportFilterSections({
    originId: _get(requestAirports, `${activeIndex}.${cityIndex.flyFrom}`, ""),
    state: originFilterState,
    resources,
  });
  const destinationAirports = getAirportFilterSections({
    originId: _get(requestAirports, `${activeIndex}.${cityIndex.arriveIn}`, ""),
    state: destinationFilterState,
    resources,
  });

  return (
    <Styled.Wrapper>
      {!_isEmpty(tabs) && (
        <FilterTabs
          tabs={tabs}
          activeIndex={activeIndex}
          testId={testId}
          onClick={setActiveIndex}
        />
      )}
      <OptionsGroup
        testId={testId}
        title={t("Filters.FlyFrom")}
        cityName={resources.getAirportCity(
          _get(requestAirports, `${activeIndex}.${cityIndex.flyFrom}`, ""),
        )}
        airports={originAirports}
        type={FLIGHT_LEG_TYPE_MAP.ORIGIN}
        onSelect={handleSelect}
      />
      <OptionsGroup
        testId={testId}
        title={t("Filters.Arrivein")}
        cityName={resources.getAirportCity(
          _get(requestAirports, `${activeIndex}.${cityIndex.arriveIn}`, ""),
        )}
        airports={destinationAirports}
        type={FLIGHT_LEG_TYPE_MAP.DESTINATION}
        onSelect={handleSelect}
      />
    </Styled.Wrapper>
  );
}

export default AirportFilter;
